import { contentAi } from '@mi-front/api-clients';
import { Wizard, useWizard } from 'react-use-wizard';
import { contentAiSteps, AiArticlesProgressProvider } from './steps/ContentAiSteps';
import { WizardFooter } from './wizard/WizardFooter';
import { WizardHeader } from './wizard/WizardHeader';
import { Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import { useHistory } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { OutlinedButton } from './components';

// Wrapper component to capture goToStep from wizard context
function StepNavigationCapture({
  goToStepRef,
  children,
}: {
  goToStepRef: React.MutableRefObject<((stepIndex: number) => void) | null>;
  children: React.ReactNode;
}) {
  const { goToStep } = useWizard();

  useEffect(() => {
    goToStepRef.current = goToStep;
  }, [goToStep, goToStepRef]);

  return <>{children}</>;
}

export function ContentWizard({ content }: { content: contentAi.ContentDto }) {
  const history = useHistory();
  const { $t } = useIntl();
  let initialStep = 0;

  if (content.socialVersions.versions.length) {
    initialStep = 2;
  } else if (content.userArticle.data) {
    initialStep = 1;
  }

  const handleBack = () => {
    history.goBack();
  };

  // Progress state for ai-articles step
  const [progress, setProgress] = useState(content.aiArticles.data ? 66 : 33);
  const [activeWizardStep, setActiveWizardStep] = useState(initialStep);
  const showRegenerateButton = activeWizardStep === 0 && progress === 66;
  const showBackToArticlesButton = activeWizardStep === 1;

  // Ref to store goToStep function from wizard
  const goToStepRef = useRef<((stepIndex: number) => void) | null>(null);

  return (
    <AiArticlesProgressProvider value={{ progress, setProgress }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          padding: '32px 24px',
          backgroundColor: '#F9FAFB',
        }}
      >
        {/* Back Button and Regenerate Button */}
        <Box
          sx={{
            height: '44px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <OutlinedButton
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              padding: '8px 12px',
              backgroundColor: '#ffffff',
              height: '44px',
              '&:hover': {
                backgroundColor: '#F7F8F9',
                border: '1px solid #636B75',
              },
            }}
          >
            {$t({
              id: 'common.actions.back',
              defaultMessage: 'Atrás',
            })}
          </OutlinedButton>

          {showRegenerateButton && (
            <OutlinedButton
              onClick={() => {
                // This will trigger the regenerate via content change
                // The actual regenerate logic is in the AiArticlesStep component
                window.dispatchEvent(new CustomEvent('regenerate-articles'));
              }}
              startIcon={<CheckIcon />}
              sx={{
                padding: '8px 12px',
                backgroundColor: '#ffffff',
                height: '44px',
                '&:hover': {
                  backgroundColor: '#F7F8F9',
                  border: '1px solid #636B75',
                },
              }}
            >
              {$t({
                id: 'contentAi.regenerateArticles',
                defaultMessage: 'Volver a generar artículos',
              })}
            </OutlinedButton>
          )}

          {showBackToArticlesButton && (
            <OutlinedButton
              onClick={() => {
                // Navigate back to ai-articles step
                if (goToStepRef.current) {
                  goToStepRef.current(0);
                }
              }}
              startIcon={<CheckIcon />}
              sx={{
                padding: '8px 12px',
                backgroundColor: '#ffffff',
                height: '44px',
                '&:hover': {
                  backgroundColor: '#F7F8F9',
                  border: '1px solid #636B75',
                },
              }}
            >
              {$t({
                id: 'contentAi.backToArticleSelection',
                defaultMessage: 'Volver a selección de artículos',
              })}
            </OutlinedButton>
          )}
        </Box>

        {/* Wizard with direct children - compatible with react-use-wizard */}
        <Wizard
          startIndex={initialStep}
          header={<WizardHeader steps={contentAiSteps} />}
          footer={<WizardFooter />}
          onStepChange={(stepIndex) => setActiveWizardStep(stepIndex)}
        >
          {contentAiSteps.map((step, index) =>
            index === 0 || index === 1 ? (
              <StepNavigationCapture key={step.id} goToStepRef={goToStepRef}>
                <step.Content content={content} />
              </StepNavigationCapture>
            ) : (
              <step.Content key={step.id} content={content} />
            ),
          )}
        </Wizard>
      </Box>
    </AiArticlesProgressProvider>
  );
}
